import {
  LOCATION_EVENT_TYPES_ENUM,
  LOCATION_SCHEDULE_TYPES_ENUM,
} from '../../../../shared/constants'

// Valid event types for temporary events
const VALID_EVENT_TYPES = Object.values(LOCATION_EVENT_TYPES_ENUM)

/**
 * Validates if a string is a valid date in YYYY-MM-DD format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid date
 */
const isValidDate = dateString => {
  if (!dateString || !dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return false
  }
  const date = new Date(dateString)
  return date.toISOString().slice(0, 10) === dateString
}

/**
 * Validates if a string is a valid 24-hour time format (HH:MM)
 * @param {string} timeString - Time string to validate
 * @returns {boolean} - True if valid time
 */
const isValid24HourTime = timeString => {
  if (!timeString) return false
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
  return timeRegex.test(timeString)
}

/**
 * Converts time string to minutes for comparison
 * @param {string} timeString - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
const timeToMinutes = timeString => {
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Validates CSV data for location import
 * @param {Array} csvData - Parsed CSV data from processCsvFile
 * @returns {Object} - { isValid: boolean, errors: string[], locations: Array }
 */
export const validateLocationsCsv = (csvData, availableLocationTypes) => {
  const validationErrors = []
  const locations = []

  // Track names to check for duplicates
  const nameTracker = new Map() // name -> array of row numbers

  if (csvData.data.length > 300) {
    validationErrors.push(
      'Row 1: The import is limited to a maximum of 300 locations per file',
    )
    return {
      isValid: false,
      errors: validationErrors,
      locations: [],
    }
  }

  csvData.data.forEach((row, index) => {
    // Skip empty rows
    if (
      !Object.values(row).some(value => value && value.toString().trim() !== '')
    ) {
      return
    }

    const rowNumber = index + 2 // Account for header row
    const errors = []

    // Required fields for all locations
    if (!row.name || !row.name.trim()) {
      errors.push(`Row ${rowNumber}: Name is required`)
    } else {
      // Track names for duplicate checking
      const trimmedName = row.name.trim().toLowerCase()
      if (nameTracker.has(trimmedName)) {
        nameTracker.get(trimmedName).push(rowNumber)
      } else {
        nameTracker.set(trimmedName, [rowNumber])
      }
    }
    if (!row.label || !row.label.trim()) {
      errors.push(`Row ${rowNumber}: Label is required`)
    }
    if (!row.type || !row.type.trim()) {
      errors.push(`Row ${rowNumber}: Type is required`)
    } else if (!availableLocationTypes.includes(row.type.trim())) {
      errors.push(
        `Row ${rowNumber}: Type must be one of: ${availableLocationTypes.join(
          ', ',
        )}`,
      )
    }
    if (!row.latitude || isNaN(parseFloat(row.latitude))) {
      errors.push(`Row ${rowNumber}: Valid latitude is required`)
    }
    if (!row.longitude || isNaN(parseFloat(row.longitude))) {
      errors.push(`Row ${rowNumber}: Valid longitude is required`)
    }
    if (!['TRUE', 'FALSE'].includes(row.is_sheltered)) {
      errors.push(`Row ${rowNumber}: is_sheltered must be TRUE or FALSE`)
    }
    if (!['TRUE', 'FALSE'].includes(row.needs_permit)) {
      errors.push(`Row ${rowNumber}: needs_permit must be TRUE or FALSE`)
    }
    if (!['TRUE', 'FALSE'].includes(row.is_temporary_event)) {
      errors.push(`Row ${rowNumber}: is_temporary_event must be TRUE or FALSE`)
    }

    const isTemporaryEvent = row.is_temporary_event === 'TRUE'

    if (isTemporaryEvent) {
      // Validate event fields
      if (!row.event_type || !row.event_type.trim()) {
        errors.push(
          `Row ${rowNumber}: event_type is required for temporary events`,
        )
      } else if (!VALID_EVENT_TYPES.includes(row.event_type.trim())) {
        errors.push(
          `Row ${rowNumber}: event_type must be one of: ${VALID_EVENT_TYPES.join(
            ', ',
          )}`,
        )
      }
      if (!row.event_description || !row.event_description.trim()) {
        errors.push(
          `Row ${rowNumber}: event_description is required for temporary events`,
        )
      }
      // Validate event dates
      if (!row.event_start_date || !isValidDate(row.event_start_date)) {
        errors.push(
          `Row ${rowNumber}: event_start_date is required and must be a valid date in YYYY-MM-DD format`,
        )
      }
      if (!row.event_end_date || !isValidDate(row.event_end_date)) {
        errors.push(
          `Row ${rowNumber}: event_end_date is required and must be a valid date in YYYY-MM-DD format`,
        )
      }

      // Validate date range (start should be same or before end)
      if (
        row.event_start_date &&
        row.event_end_date &&
        isValidDate(row.event_start_date) &&
        isValidDate(row.event_end_date)
      ) {
        const startDate = new Date(row.event_start_date)
        const endDate = new Date(row.event_end_date)
        if (startDate > endDate) {
          errors.push(
            `Row ${rowNumber}: event_start_date must be same or before event_end_date`,
          )
        }
      }

      if (!row.event_opening_hour_1 || !row.event_closing_hour_1) {
        errors.push(
          `Row ${rowNumber}: At least event_opening_hour_1 and event_closing_hour_1 are required for temporary events`,
        )
      }

      // Validate event time ranges
      const timeRanges = []
      for (let i = 1; i <= 3; i++) {
        const openingHour = row[`event_opening_hour_${i}`]
        const closingHour = row[`event_closing_hour_${i}`]

        if (openingHour || closingHour) {
          // Both opening and closing hours must be provided together
          if (!openingHour || !closingHour) {
            errors.push(
              `Row ${rowNumber}: Both event_opening_hour_${i} and event_closing_hour_${i} must be provided together`,
            )
            continue
          }

          // Validate 24-hour time format
          if (!isValid24HourTime(openingHour)) {
            errors.push(
              `Row ${rowNumber}: event_opening_hour_${i} must be in 24-hour format (HH:MM)`,
            )
          }
          if (!isValid24HourTime(closingHour)) {
            errors.push(
              `Row ${rowNumber}: event_closing_hour_${i} must be in 24-hour format (HH:MM)`,
            )
          }

          if (
            isValid24HourTime(openingHour) &&
            isValid24HourTime(closingHour)
          ) {
            const openingMinutes = timeToMinutes(openingHour)
            const closingMinutes = timeToMinutes(closingHour)

            // Validate start time is before end time
            if (openingMinutes >= closingMinutes) {
              errors.push(
                `Row ${rowNumber}: event_opening_hour_${i} must be before event_closing_hour_${i}`,
              )
            }

            // Validate minimum 3-hour range
            const durationMinutes = closingMinutes - openingMinutes
            if (durationMinutes < 180) {
              // 3 hours = 180 minutes
              errors.push(
                `Row ${rowNumber}: Time range ${i} must be at least 3 hours (event_opening_hour_${i} to event_closing_hour_${i})`,
              )
            }

            // Store valid ranges for overlap checking
            if (openingMinutes < closingMinutes) {
              timeRanges.push({
                index: i,
                start: openingMinutes,
                end: closingMinutes,
              })
            }
          }
        }
      }

      // Check for overlaps between time ranges
      for (let i = 0; i < timeRanges.length; i++) {
        for (let j = i + 1; j < timeRanges.length; j++) {
          const range1 = timeRanges[i]
          const range2 = timeRanges[j]

          // Check if ranges overlap
          if (
            (range1.start >= range2.start && range1.start <= range2.end) ||
            (range1.end >= range2.start && range1.end <= range2.end) ||
            (range2.start >= range1.start && range2.start <= range1.end) ||
            (range2.end >= range1.start && range2.end <= range1.end)
          ) {
            errors.push(
              `Row ${rowNumber}: Time ranges ${range1.index} and ${
                range2.index
              } overlap`,
            )
          }
        }
      }
    } else {
      // Validate time availability fields
      if (!['TRUE', 'FALSE'].includes(row.morning)) {
        errors.push(`Row ${rowNumber}: morning must be TRUE or FALSE`)
      }
      if (!['TRUE', 'FALSE'].includes(row.lunch)) {
        errors.push(`Row ${rowNumber}: lunch must be TRUE or FALSE`)
      }
      if (!['TRUE', 'FALSE'].includes(row.afternoon)) {
        errors.push(`Row ${rowNumber}: afternoon must be TRUE or FALSE`)
      }

      // Validate day fields
      const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ]
      days.forEach(day => {
        if (!['TRUE', 'FALSE'].includes(row[day])) {
          errors.push(`Row ${rowNumber}: ${day} must be TRUE or FALSE`)
        }
      })

      // Validate that at least one time period is selected
      const hasAnyTimeSelected =
        row.morning === 'TRUE' ||
        row.lunch === 'TRUE' ||
        row.afternoon === 'TRUE'

      if (!hasAnyTimeSelected) {
        errors.push(
          `Row ${rowNumber}: At least one time period (morning, lunch, or afternoon) must be TRUE`,
        )
      }

      // Validate that at least one day is selected
      const hasAnyDaySelected = days.some(day => row[day] === 'TRUE')

      if (!hasAnyDaySelected) {
        errors.push(
          `Row ${rowNumber}: At least one day of the week must be TRUE`,
        )
      }
    }

    if (errors.length > 0) {
      validationErrors.push(...errors)
    } else {
      // If no errors, add to locations array
      locations.push({
        name: row.name.trim(),
        label: row.label.trim(),
        type: row.type.trim(),
        latitude: parseFloat(row.latitude),
        longitude: parseFloat(row.longitude),
        isSheltered: row.is_sheltered === 'TRUE',
        needsPermit: row.needs_permit === 'TRUE',
        isTemporaryEvent: row.is_temporary_event === 'TRUE',
        // Time availability (only for non-temporary events)
        morning: !isTemporaryEvent ? row.morning === 'TRUE' : null,
        lunch: !isTemporaryEvent ? row.lunch === 'TRUE' : null,
        afternoon: !isTemporaryEvent ? row.afternoon === 'TRUE' : null,
        // Days of week (only for non-temporary events)
        monday: !isTemporaryEvent ? row.monday === 'TRUE' : null,
        tuesday: !isTemporaryEvent ? row.tuesday === 'TRUE' : null,
        wednesday: !isTemporaryEvent ? row.wednesday === 'TRUE' : null,
        thursday: !isTemporaryEvent ? row.thursday === 'TRUE' : null,
        friday: !isTemporaryEvent ? row.friday === 'TRUE' : null,
        saturday: !isTemporaryEvent ? row.saturday === 'TRUE' : null,
        sunday: !isTemporaryEvent ? row.sunday === 'TRUE' : null,
        // Event details (only for temporary events)
        eventType: isTemporaryEvent
          ? (row.event_type && row.event_type.trim()) || ''
          : null,
        eventDescription: isTemporaryEvent
          ? (row.event_description && row.event_description.trim()) || ''
          : null,
        eventStartDate: isTemporaryEvent ? row.event_start_date || '' : null,
        eventEndDate: isTemporaryEvent ? row.event_end_date || '' : null,
        eventOpeningHour1: isTemporaryEvent
          ? row.event_opening_hour_1 || ''
          : null,
        eventClosingHour1: isTemporaryEvent
          ? row.event_closing_hour_1 || ''
          : null,
        eventOpeningHour2: isTemporaryEvent
          ? row.event_opening_hour_2 || ''
          : null,
        eventClosingHour2: isTemporaryEvent
          ? row.event_closing_hour_2 || ''
          : null,
        eventOpeningHour3: isTemporaryEvent
          ? row.event_opening_hour_3 || ''
          : null,
        eventClosingHour3: isTemporaryEvent
          ? row.event_closing_hour_3 || ''
          : null,
      })
    }
  })

  // Check for duplicate names
  nameTracker.forEach((rowNumbers, name) => {
    if (rowNumbers.length > 1) {
      rowNumbers.forEach(rowNumber => {
        validationErrors.push(
          `Row ${rowNumber}: Name "${name}" is duplicated (also appears in rows: ${rowNumbers
            .filter(r => r !== rowNumber)
            .join(', ')})`,
        )
      })
    }
  })

  return {
    isValid: validationErrors.length === 0,
    errors: validationErrors,
    locations,
  }
}

export const prepareDataSetForImport = locations => {
  return locations.map(location => {
    if (!location.isTemporaryEvent) {
      return {
        name: location.name,
        label: location.label,
        type: location.type,
        latitude: location.latitude,
        longitude: location.longitude,
        isSheltered: location.isSheltered,
        isEvent: location.isTemporaryEvent,
        hasPermit: !location.needsPermit,
        description: location.eventDescription,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.TIME_SLOTS,
        morning: location.morning,
        lunch: location.lunch,
        afternoon: location.afternoon,
        slotsScheduleDays: convertDaysToArrayOfDaysNames(location),
      }
    } else {
      return {
        name: location.name,
        label: location.label,
        type: location.type,
        latitude: location.latitude,
        longitude: location.longitude,
        isSheltered: location.isSheltered,
        isEvent: location.isTemporaryEvent,
        hasPermit: !location.needsPermit,
        description: location.eventDescription,
        eventType: location.eventType,
        scheduleType: LOCATION_SCHEDULE_TYPES_ENUM.EVENT,
        eventStartDate: location.eventStartDate,
        eventEndDate: location.eventEndDate,
        eventOperationalTimes: convertTimeRangesToArrayOfTimeRanges(location),
      }
    }
  })
}

const convertDaysToArrayOfDaysNames = location => {
  let days = []
  if (location.monday) {
    days.push('monday')
  }
  if (location.tuesday) {
    days.push('tuesday')
  }
  if (location.wednesday) {
    days.push('wednesday')
  }

  if (location.thursday) {
    days.push('thursday')
  }
  if (location.friday) {
    days.push('friday')
  }
  if (location.saturday) {
    days.push('saturday')
  }
  if (location.sunday) {
    days.push('sunday')
  }
  return days
}

const convertTimeRangesToArrayOfTimeRanges = location => {
  let timeRanges = []
  if (location.eventOpeningHour1 && location.eventClosingHour1) {
    timeRanges.push({
      start: location.eventOpeningHour1,
      end: location.eventClosingHour1,
    })
  }
  if (location.eventOpeningHour2 && location.eventClosingHour2) {
    timeRanges.push({
      start: location.eventOpeningHour2,
      end: location.eventClosingHour2,
    })
  }
  if (location.eventOpeningHour3 && location.eventClosingHour3) {
    timeRanges.push({
      start: location.eventOpeningHour3,
      end: location.eventClosingHour3,
    })
  }

  return timeRanges
}
